<template>
  <transition
    name="content"
    @enter="enter"
    @leave="leave"
    @after-enter="afterEnter"
    @after-leave="afterLeave"
  >
    <div v-show="show">
      <slot></slot>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * ゆっくりと開閉するコンポーネント
 * @slot 表示するコンテンツ
 * @prop show 表示するかどうか
 * 参考：
 *  - https://ja.vuejs.org/guide/built-ins/transition
 *  - https://note.com/noliaki/n/n6e3a60748c11
 */

type Props = {
  show: boolean;
};

type RequestAnimationFrameCallback = () => void;

type El = {
  style: {
    overflow: string;
    height: string;
  };
  scrollHeight: number;
};

defineProps<Props>();

const nextFrame = (fn: RequestAnimationFrameCallback) => {
  window.requestAnimationFrame(() => window.requestAnimationFrame(fn));
};

const enter = (el: El) => {
  el.style.overflow = 'hidden';
  el.style.height = '0';

  nextFrame(() => {
    el.style.height = `${el.scrollHeight}px`;
  });
};

const leave = (el: El) => {
  el.style.overflow = 'hidden';
  el.style.height = `${el.scrollHeight}px`;

  nextFrame(() => {
    el.style.height = '0';
  });
};

const afterEnter = (el: El) => {
  el.style.overflow = '';
  el.style.height = '';
};

const afterLeave = (el: El) => {
  el.style.overflow = '';
  el.style.height = '';
};
</script>

<style scoped>
.content-enter-active,
.content-leave-active {
  /* 支給デザインの `slideToggle(300)` に合わせる（決定#41）。
     `design-src/20260806/assets/js/script_main.js:65` ほか、支給JSの開閉はすべて 300ms */
  transition: height 300ms ease;
}
</style>
